import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useStickyHeaderHeight } from '@/hooks/useStickyHeaderHeight';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, ChevronDown, KeyRound, LogOut, Menu, MessageCircle, Moon, MoreHorizontal, Search,
  ShieldCheck, Sun, User as UserIcon, X, Languages,
} from 'lucide-react';

import styles from './TopBar.module.css';
import { useAuth } from '@/contexts/shared/AuthContext';
import { useTheme } from '@/contexts/shared/ThemeContext';
import { useImpersonation } from '@/hooks/admin/AuditManagement/useImpersonation';
import { useInstalledLanguages } from '@/hooks/useInstalledLanguages';
import { useLanguage, tr } from '@/i18n';
import { roleLabel } from '@/i18n/roleLabels';
import { useMultilang } from '@/hooks/useMultilang';
import { SiteMenuService, SiteMenuItem } from '@/services/site-menu.service';
import { useMarkingQueueCount, useOjtEvaluator, useOjtQueueCount } from '@/hooks/useSharedLearnerData';
import { DM_QUERY_KEYS, MessagingApi } from '@/services/messaging.service';
import { BrandLogo } from '../BrandLogo/BrandLogo';
import { GlobalSearch } from '../GlobalSearch/GlobalSearch';
import { NotificationBell } from '../NotificationBell/NotificationBell';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { Avatar } from '@/components/ds';
import { AdminMenu } from './AdminMenu';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { CHANGE_PASSWORD_PATH, signsInWithPassword } from '@/auth/passwordChange';

// Shown while the site menu loads, or when the tenant has no menu rows. Built per render
// (not at module load) so the labels follow the active language once its strings arrive.
const fallbackItems = (): SiteMenuItem[] => [
  { id: -1, tenant_id: null, label: tr("breadcrumb.dashboard", "Dashboard"), icon: null, url: '/user/dashboard', tooltip: tr("breadcrumb.dashboard", "Dashboard"), sort_order: 0, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -2, tenant_id: null, label: tr("ui.explore", "Explore"), icon: null, url: '/user/courses', tooltip: tr("ui.explore", "Explore"), sort_order: 1, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -3, tenant_id: null, label: tr("ui.my_learning", "My Learning"), icon: null, url: '/user/my-courses', tooltip: tr("ui.my_learning_e7f108", "My learning"), sort_order: 2, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -5, tenant_id: null, label: tr("ui.goals", "Goals"), icon: null, url: '/user/goals', tooltip: tr("ui.goals", "Goals"), sort_order: 4, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
  { id: -8, tenant_id: null, label: tr("course.tabs.skills", "Skills"), icon: null, url: '/user/skills', tooltip: tr("ui.my_skills_e8f13d", "My skills"), sort_order: 5, is_active: true, open_in_new_tab: false, created_at: 0, updated_at: 0, visibility_rules: [] },
];

const MANAGER_URLS = ['/user/team-hub', '/user/my-team'];

type NavEntry =
  | { kind: 'link'; key: string; label: string; url: string; newTab: boolean; badge?: number }
  | { kind: 'group'; key: string; label: string; children: Array<{ key: string; label: string; url: string; newTab: boolean }> };

const isActivePath = (pathname: string, url: string) =>
  url === '/' ? pathname === '/' : pathname === url || pathname.startsWith(url.endsWith('/') ? url : `${url}/`);

export const TopBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ml } = useMultilang();
  const { user, logout, isImpersonating } = useAuth();
  const { returnToAccount, isSubmitting } = useImpersonation();
  const { theme, setTheme, allowedThemes } = useTheme();
  const { languages } = useInstalledLanguages();
  const { locale, setLocale, strings } = useLanguage();

  const [loadedItems, setItems] = useState<SiteMenuItem[] | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `strings` re-resolves the fallback labels
  const items = useMemo(() => loadedItems ?? fallbackItems(), [loadedItems, strings]);
  const [openMenu, setOpenMenu] = useState<string | null>(null); // group key | 'more' | 'admin' | 'profile'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    SiteMenuService.getUserMenu()
      .then((res) => { if (!cancelled && res.results?.length) setItems(res.results); })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);
  // Marking / OJT counts and the evaluator flag — shared with the sidebar and the Tasks widget.
  const markCount = useMarkingQueueCount().data ?? 0;
  const ojtCount = useOjtQueueCount().data ?? 0;
  const ojtEvaluator = useOjtEvaluator().data ?? false;

  const { data: dmUnread } = useQuery({
    queryKey: DM_QUERY_KEYS.unread,
    queryFn: MessagingApi.unreadCount,
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const dmBadge = dmUnread?.threads ?? 0;

  // Close popovers on navigation.
  useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // ── Menu model ────────────────────────────────────────────────────────────
  const { entries, isManager } = useMemo(() => {
    const childrenByParent = new Map<number, SiteMenuItem[]>();
    items.forEach((i) => {
      if (i.parent_id != null && i.url) {
        if (!childrenByParent.has(i.parent_id)) childrenByParent.set(i.parent_id, []);
        childrenByParent.get(i.parent_id)!.push(i);
      }
    });
    const out: NavEntry[] = [];
    let manager = false;
    items.forEach((i) => {
      if (i.url && MANAGER_URLS.some((u) => i.url!.startsWith(u))) manager = true;
      if (i.parent_id != null && i.url) return;
      if (!i.url) {
        const kids = childrenByParent.get(i.id) ?? [];
        if (kids.some((k) => MANAGER_URLS.some((u) => k.url!.startsWith(u)))) manager = true;
        if (kids.length) {
          out.push({
            kind: 'group',
            key: `g${i.id}`,
            label: ml(i.label),
            children: kids.map((k) => ({ key: `l${k.id}`, label: ml(k.label), url: k.url!, newTab: k.open_in_new_tab })),
          });
        }
        return;
      }
      out.push({ kind: 'link', key: `l${i.id}`, label: ml(i.label), url: i.url, newTab: i.open_in_new_tab });
    });
    if (markCount > 0) out.push({ kind: 'link', key: 'marking', label: tr("ui.essay_marking", "Essay marking"), url: '/user/marking', newTab: false, badge: markCount });
    if (ojtCount > 0 || ojtEvaluator) out.push({ kind: 'link', key: 'ojt', label: tr("ui.ojt_evaluations", "OJT evaluations"), url: '/user/ojt-evaluations', newTab: false, badge: ojtCount || undefined });
    return { entries: out, isManager: manager };
  }, [items, ml, markCount, ojtCount, ojtEvaluator]);

  // Administrators — and course creators (also category-only ones), who get just their course pages.
  const { showAdmin: hasAdminAccess } = useAdminAccess();
  const inManagerView = MANAGER_URLS.some((u) => location.pathname.startsWith(u));
  const inAdmin = location.pathname.startsWith('/admin');

  // ── Overflow: fit as many entries as the nav has room for; rest → More ────
  const navRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(entries.length);

  const recompute = useCallback(() => {
    const nav = navRef.current;
    const meas = measureRef.current;
    if (!nav || !meas) return;
    const avail = nav.clientWidth;
    const widths = Array.from(meas.children).map((c) => (c as HTMLElement).offsetWidth);
    const moreW = widths.pop() ?? 0; // last measured child is the More button
    const gap = 4;
    let used = 0;
    let fit = 0;
    for (let i = 0; i < widths.length; i++) {
      const next = used + widths[i] + (i ? gap : 0);
      const needMore = i < widths.length - 1;
      if (next + (needMore ? gap + moreW : 0) <= avail) { used = next; fit = i + 1; } else break;
    }
    setVisibleCount(fit);
  }, []);

  useLayoutEffect(() => { recompute(); }, [entries, recompute]);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(nav);
    document.fonts?.ready?.then(() => recompute()).catch(() => undefined);
    return () => ro.disconnect();
  }, [recompute]);

  const shown = entries.slice(0, visibleCount);
  const overflow = entries.slice(visibleCount);

  // ── Popover dismissal ─────────────────────────────────────────────────────
  const barRef = useRef<HTMLElement>(null);
  useStickyHeaderHeight(barRef);  // phones: pinned page content stops below the sticky top bar
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-topbar-pop]') || t.closest('[data-topbar-trigger]')) return;
      setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  const toggle = (key: string) => setOpenMenu((m) => (m === key ? null : key));

  if (!user) {
    return (
      <header className={styles.bar}>
        <div className={styles.brand}><BrandLogo className={styles.logo} /></div>
      </header>
    );
  }

  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.username || tr("ui.user_x_a446ce", "User #{v0}", { v0: user.id });
  const roleText = roleLabel(user.roles?.[0]?.name) || tr("audit.col_user", "User");
  const showThemeToggle = allowedThemes.includes('light') && allowedThemes.includes('dark');

  const renderLink = (e: Extract<NavEntry, { kind: 'link' }>, extraClass = '') => {
    const content = (
      <>
        <span>{e.label}</span>
        {e.badge ? <span className={styles.navBadge}>{e.badge > 99 ? '99+' : e.badge}</span> : null}
      </>
    );
    if (e.newTab) {
      return <a key={e.key} href={e.url} target="_blank" rel="noopener noreferrer" className={`${styles.navLink} ${extraClass}`}>{content}</a>;
    }
    return (
      <NavLink
        key={e.key}
        to={e.url}
        className={() => `${styles.navLink} ${isActivePath(location.pathname, e.url) ? styles.navActive : ''} ${extraClass}`}
        aria-current={isActivePath(location.pathname, e.url) ? 'page' : undefined}
      >
        {content}
      </NavLink>
    );
  };

  const renderGroup = (g: Extract<NavEntry, { kind: 'group' }>) => {
    const active = g.children.some((c) => isActivePath(location.pathname, c.url));
    const open = openMenu === g.key;
    return (
      <div key={g.key} className={styles.navGroup}>
        <button
          type="button"
          data-topbar-trigger
          className={`${styles.navLink} ${active ? styles.navActive : ''}`}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => toggle(g.key)}
        >
          <span>{g.label}</span>
          <ChevronDown size={16} className={`${styles.chev} ${open ? styles.chevOpen : ''}`} aria-hidden="true" />
        </button>
        {open && (
          <div className={styles.menu} role="menu" data-topbar-pop>
            {g.children.map((c) =>
              c.newTab ? (
                <a key={c.key} role="menuitem" href={c.url} target="_blank" rel="noopener noreferrer" className={styles.menuItem}>{c.label}</a>
              ) : (
                <Link
                  key={c.key}
                  role="menuitem"
                  to={c.url}
                  className={`${styles.menuItem} ${isActivePath(location.pathname, c.url) ? styles.menuItemActive : ''}`}
                >
                  {c.label}
                </Link>
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const drawer = drawerOpen && createPortal(
    <div className={styles.drawerRoot}>
      <div className={styles.drawerBackdrop} onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      <nav className={styles.drawer} aria-label={tr("ui.main", "Main")}>
        <div className={styles.drawerHead}>
          <BrandLogo className={styles.logo} />
          <button type="button" className={styles.iconBtn} onClick={() => setDrawerOpen(false)} aria-label={tr("ui.close_menu", "Close menu")}><X size={20} /></button>
        </div>
        {entries.map((e) =>
          e.kind === 'link' ? (
            renderLink(e, styles.drawerLink)
          ) : (
            <div key={e.key} className={styles.drawerGroup}>
              <span className={styles.drawerGroupLabel}>{e.label}</span>
              {e.children.map((c) => (
                <Link key={c.key} to={c.url} className={`${styles.navLink} ${styles.drawerLink} ${isActivePath(location.pathname, c.url) ? styles.navActive : ''}`}>{c.label}</Link>
              ))}
            </div>
          ),
        )}
        {hasAdminAccess && (
          <Link to="/admin" className={`${styles.navLink} ${styles.drawerLink} ${inAdmin ? styles.navActive : ''}`} onClick={(ev) => { ev.preventDefault(); setDrawerOpen(false); setOpenMenu('admin'); }}>
            {tr("ui.admin", "Admin")}
          </Link>
        )}
      </nav>
    </div>,
    document.body,
  );

  return (
    <>
      {isImpersonating && (
        <div className={styles.impersonation} role="status">
          <span>
            {tr("ui.you_are_logged_in_as", "You are logged in as")} <strong>{fullName}</strong>{tr("ui.original_user_sep", ". Original user:")}{' '}
            <strong>{localStorage.getItem('original_admin_name') || tr("tenant.users.role_admin", "Administrator")}</strong>
          </span>
          <button type="button" className={styles.impersonationBtn} onClick={returnToAccount} disabled={isSubmitting}>
            {isSubmitting ? tr("ui.returning", "Returning…") : tr("ui.return_to_my_account", "Return to my account")}
          </button>
        </div>
      )}

      <header className={styles.bar} ref={barRef}>
        <button type="button" className={`${styles.iconBtn} ${styles.burger}`} onClick={() => setDrawerOpen(true)} aria-label={tr("ui.open_navigation_menu", "Open navigation menu")}>
          <Menu size={20} />
        </button>

        <Link to="/user/dashboard" className={styles.brand} aria-label={tr("ui.home", "Home")}>
          <BrandLogo className={styles.logo} />
        </Link>

        <nav className={styles.nav} aria-label={tr("ui.main", "Main")} ref={navRef}>
          {shown.map((e) => (e.kind === 'link' ? renderLink(e) : renderGroup(e)))}
          {overflow.length > 0 && (
            <div className={styles.navGroup}>
              <button
                type="button"
                data-topbar-trigger
                className={`${styles.navLink} ${overflow.some((e) => (e.kind === 'link' ? isActivePath(location.pathname, e.url) : e.children.some((c) => isActivePath(location.pathname, c.url)))) ? styles.navActive : ''}`}
                aria-expanded={openMenu === 'more'}
                aria-haspopup="menu"
                onClick={() => toggle('more')}
              >
                <MoreHorizontal size={18} aria-hidden="true" />
                <span>{tr("ui.more", "More")}</span>
              </button>
              {openMenu === 'more' && (
                <div className={`${styles.menu} ${styles.menuEnd}`} role="menu" data-topbar-pop>
                  {overflow.map((e) =>
                    e.kind === 'link' ? (
                      <Link key={e.key} role="menuitem" to={e.url} className={`${styles.menuItem} ${isActivePath(location.pathname, e.url) ? styles.menuItemActive : ''}`}>
                        {e.label}
                        {e.badge ? <span className={styles.navBadge}>{e.badge}</span> : null}
                      </Link>
                    ) : (
                      <div key={e.key} className={styles.menuGroup}>
                        <span className={styles.menuGroupLabel}>{e.label}</span>
                        {e.children.map((c) => (
                          <Link key={c.key} role="menuitem" to={c.url} className={`${styles.menuItem} ${isActivePath(location.pathname, c.url) ? styles.menuItemActive : ''}`}>{c.label}</Link>
                        ))}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          )}

          {/* Off-screen copy used only to measure each entry's natural width. */}
          <div className={styles.measure} ref={measureRef} aria-hidden="true">
            {entries.map((e) => (
              <span key={e.key} className={styles.navLink}>
                <span>{e.label}</span>
                {e.kind === 'group' && <ChevronDown size={16} />}
                {e.kind === 'link' && e.badge ? <span className={styles.navBadge}>{e.badge}</span> : null}
              </span>
            ))}
            <span className={styles.navLink}><MoreHorizontal size={18} /><span>{tr("ui.more", "More")}</span></span>
          </div>
        </nav>

        {hasAdminAccess && (
          <div className={`${styles.navGroup} ${styles.hideOnPhone}`}>
            <button
              type="button"
              data-topbar-trigger
              className={`${styles.adminBtn} ${inAdmin ? styles.adminActive : ''}`}
              aria-expanded={openMenu === 'admin'}
              aria-haspopup="dialog"
              onClick={() => toggle('admin')}
            >
              <ShieldCheck size={18} aria-hidden="true" />
              <span>{tr("ui.admin", "Admin")}</span>
            </button>
          </div>
        )}

        {isManager && (
          <div className={styles.viewSwitch} role="group" aria-label={tr("ui.view_as", "View as")}>
            <Link to="/user/dashboard" className={`${styles.viewOpt} ${!inManagerView ? styles.viewOptOn : ''}`} aria-current={!inManagerView ? 'page' : undefined}>{tr("ui.employee", "Employee")}</Link>
            <Link to="/user/team-hub" className={`${styles.viewOpt} ${inManagerView ? styles.viewOptOn : ''}`} aria-current={inManagerView ? 'page' : undefined}>{tr("ui.manager", "Manager")}</Link>
          </div>
        )}

        <div className={`${styles.search} ${searchOpen ? styles.searchOpen : ''}`}>
          <GlobalSearch className={styles.searchField} placeholder={tr("ui.search_courses", "Search courses")} />
        </div>

        <div className={styles.actions}>
          <button type="button" className={`${styles.iconBtn} ${styles.searchToggle}`} onClick={() => setSearchOpen((v) => !v)} aria-label={tr("ui.search_133484", "Search")} aria-expanded={searchOpen}>
            <Search size={20} />
          </button>
          <button type="button" className={`${styles.iconBtn} ${styles.hideOnPhone}`} onClick={() => navigate('/user/messages')} aria-label={dmBadge ? tr("ui.messages_x_unread", "Messages, {v0} unread", { v0: dmBadge }) : tr("ui.messages", "Messages")} title={tr("ui.messages", "Messages")}>
            <MessageCircle size={20} />
            {dmBadge > 0 && <span className={styles.iconBadge}>{dmBadge > 99 ? '99+' : dmBadge}</span>}
          </button>
          <NotificationBell />

          <div className={styles.navGroup}>
            <button
              type="button"
              data-topbar-trigger
              className={styles.profile}
              aria-expanded={openMenu === 'profile'}
              aria-haspopup="menu"
              aria-label={tr("ui.account_menu", "Account menu")}
              onClick={() => toggle('profile')}
            >
              <Avatar name={fullName} src={user.profileimageurl || null} size={40} tone="coral" />
              <span className={styles.profileText}>
                <span className={styles.profileName}>{fullName}</span>
                <span className={styles.profileSub}>{isImpersonating ? tr("ui.logged_in_as", "Logged in as") : roleText}</span>
              </span>
            </button>
            {openMenu === 'profile' && (
              <div className={`${styles.menu} ${styles.menuEnd} ${styles.profileMenu}`} role="menu" data-topbar-pop>
                <Link role="menuitem" to="/user/profile" className={styles.menuItem}><UserIcon size={18} aria-hidden="true" />{tr("ui.profile", "Profile")}</Link>
                <Link role="menuitem" to="/user/calendar" className={styles.menuItem}><CalendarDays size={18} aria-hidden="true" />{tr("ui.calendar", "Calendar")}</Link>
                {!isImpersonating && signsInWithPassword(user) && (
                  <Link role="menuitem" to={CHANGE_PASSWORD_PATH} state={{ from: location }} className={styles.menuItem}><KeyRound size={18} aria-hidden="true" />{tr("user.pw_change.action", "Change password")}</Link>
                )}
                <Link role="menuitem" to="/user/messages" className={`${styles.menuItem} ${styles.phoneOnly}`}>
                  <MessageCircle size={18} aria-hidden="true" />{tr("ui.messages", "Messages")}
                  {dmBadge > 0 && <span className={styles.navBadge}>{dmBadge > 99 ? '99+' : dmBadge}</span>}
                </Link>
                {showThemeToggle && (
                  <div className={styles.menuRow}>
                    <span className={styles.menuRowLabel}>{tr("ui.theme", "Theme")}</span>
                    <div className={styles.themeSeg} role="group" aria-label={tr("ui.colour_theme", "Colour theme")}>
                      <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')} aria-label={tr("ui.light", "Light")}><Sun size={16} /></button>
                      <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')} aria-label={tr("ui.dark", "Dark")}><Moon size={16} /></button>
                    </div>
                  </div>
                )}
                {languages.length > 1 && (
                  <div className={styles.menuGroup}>
                    <span className={styles.menuGroupLabel}><Languages size={14} aria-hidden="true" /> {tr("tags.import.lang_label", "Language")}</span>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        role="menuitemradio"
                        aria-checked={locale === lang.code}
                        className={`${styles.menuItem} ${locale === lang.code ? styles.menuItemActive : ''}`}
                        onClick={() => setLocale(lang.code)}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className={styles.menuSep} />
                <button type="button" role="menuitem" className={`${styles.menuItem} ${styles.menuDanger}`} onClick={() => { setOpenMenu(null); setLogoutOpen(true); }}>
                  <LogOut size={18} aria-hidden="true" />{tr("ui.log_out", "Log out")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {openMenu === 'admin' && hasAdminAccess && (
        <AdminMenu onClose={() => setOpenMenu(null)} />
      )}

      {drawer}

      <ConfirmModal
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => { setLogoutOpen(false); logout(); }}
        title={tr("ui.log_out", "Log out")}
        description={tr("ui.are_you_sure_you_want_to_log_out", "Are you sure you want to log out?")}
        confirmText={tr("ui.log_out", "Log out")}
        cancelText={tr("common.cancel", "Cancel")}
      />
    </>
  );
};
